import { STUDY_STATUS } from '@/entities/study';
import { handleEditPhysicianNote } from '@/entities/physician';
import { handleSaveSignature } from '@/entities/user';

import {
  uploadFileToUrl,
  showMessageError,
  showMessageInfo,
  showMessageSuccess,
} from '@/shared/lib';
import { useSetState } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { t } from 'ttag';
import { EDIT_DRAWER_KEY } from '../../../../config/editStudyDetails';
import type {
  DataProps,
  MinimalState,
  EditPhysicianAmendmentNotesProps,
  StoreData,
} from './editPhysicianAmendmentNotesDrawer.types';

/**
 * Drives the drawer where a clinic physician edits a study's amendment notes and signs them. Re-seeds
 * the note and signature whenever the study data changes, restores the stored values when the drawer
 * reopens, tracks whether the note was edited, and wires the change, signature, save-guard, save and
 * close handlers.
 *
 * @param props - onClose, open, the study data, the updateStudyData callback and the drawerKey.
 * @returns An object carrying the drawer state and setter, the aborted-study and edited-note flags,
 * and the change, signature, save-guard, close and save handlers.
 */
export const useEditPhysicianAmendmentNotesDrawer = ({
  onClose,
  open,
  data,
  updateStudyData,
  drawerKey,
}: Pick<
  EditPhysicianAmendmentNotesProps,
  'onClose' | 'open' | 'data' | 'updateStudyData' | 'drawerKey'
>) => {
  const [state, setState] = useSetState<MinimalState>({
    physiciansNote: '',
    signature: data.signatureBase64String,

    listError: {},

    isOpenSignModal: false,
    isSaving: false,
    isInitiating: false,
  });

  const previousPropsData = useRef<DataProps | null>(null);
  const storeData = useRef<StoreData>({});
  const initialNoteRef = useRef<string>('');

  const isAbortedStudy = data?.status === STUDY_STATUS.ABORTED;

  const isEditComment = state.physiciansNote.trim() !== initialNoteRef.current.trim();

  /**
   * Seeds the physician amendment notes drawer from the study being viewed. Copies the existing physician note into initialNoteRef and into storeData.current alongside the stored signature, then applies both to state and resets the errors, initiating, sign-modal and saving flags.
   *
   * @returns Nothing.
   */
  const initiateState = () => {
    setState({ isInitiating: true });

    const note = data?.info?.diagnosis?.physiciansNote ?? '';
    initialNoteRef.current = note;

    // Store the initial state in storeData to preserve the values
    storeData.current = {
      physiciansNote: note,
      signature: data.signatureBase64String,
    };

    setState({
      physiciansNote: note,
      signature: data.signatureBase64String,
      listError: {},
      isInitiating: false,
      isOpenSignModal: false,
      isSaving: false,
    });
  };

  /**
   * Applies the physician amendment note as it is typed. Coerces a nullish value to an empty string, keeps the previous text and records a length error when it exceeds 400 characters, and otherwise stores the text while dropping any existing physiciansNote error.
   *
   * @param name - Name of the form field that changed, not read.
   * @param value - New note text.
   * @returns Nothing.
   */
  const handleChange = (name: string, value: any) => {
    const next = value ?? '';
    if (next.length > 400) {
      setState({
        listError: {
          ...state.listError,
          physiciansNote: 'Maximum 400 characters allowed.',
        },
      });
      return;
    }

    // clear error when valid
    if (state.listError.physiciansNote) {
      const nextErr = { ...state.listError };
      delete nextErr.physiciansNote;
      setState({ physiciansNote: next, listError: nextErr });
      return;
    }

    setState({ physiciansNote: next });
  };

  /**
   * Captures the signature drawn in the sign modal. Stores the base64 PNG string as the current signature in state.
   *
   * @param signatureBase64String - Signature image encoded as a base64 PNG string.
   * @returns Nothing.
   */
  const onEndSignature = (signatureBase64String: string) => {
    setState({ signature: signatureBase64String });
  };

  /**
   * Collects the field errors of the physician amendment notes form. Records a physiciansNote error when the note exceeds 400 characters and returns the resulting map.
   *
   * @returns Map of field name to error message, empty when valid.
   */
  const validateData = () => {
    const listError: Record<string, string> = {};

    if ((state.physiciansNote || '').length > 400) {
      listError.physiciansNote = 'Maximum 400 characters allowed.';
    }

    return listError;
  };

  /**
   * Persists the physician amendment note together with the physician's signature. Uploads the signature as a PNG, re-saves it through handleSaveSignature when it differs from the stored one and keeps the returned URL, submits the trimmed note for the study's friendly ID, toasts success and pushes the note and signature back to the study data; a failure logs and toasts an error, and the sign modal and saving flag are always cleared.
   *
   * @returns A promise resolving once the save attempt has finished.
   */
  const onSave = async () => {
    setState({ isSaving: true });

    try {
      let signatureURL = (await uploadFileToUrl([state.signature], 'png', true)) as string;
      if (state.signature && state.signature !== data.signatureBase64String) {
        const saveSignatureResult = await handleSaveSignature(signatureURL);
        const { saveSignature } = saveSignatureResult.data || {};
        if (saveSignature?.isSuccess) {
          signatureURL = saveSignature.signature ?? signatureURL;
        }
      }
      await handleEditPhysicianNote(data.friendlyId, state.physiciansNote?.trim(), signatureURL);
      showMessageSuccess({ content: t`Physician Amendment Notes updated successfully.` });
      updateStudyData(
        {
          diagnosis: {
            physiciansNote: state.physiciansNote?.trim(),
          },
          signatureBase64String: state.signature,
        },
        EDIT_DRAWER_KEY.PHYSICIAN_NOTES,
      );

      storeData.current = {
        ...storeData.current,
        physiciansNote: state.physiciansNote?.trim(),
        signature: state.signature,
      } as any;
    } catch (error) {
      console.error('Failed to save signature: ', error);
      showMessageError({ content: t`Failed to update Physician Amendment Notes` });
    } finally {
      setState({
        isOpenSignModal: false,
        isSaving: false,
      });
    }
  };

  /**
   * Guards saving the physician amendment notes. Stops with a no-changes message when the note was not edited, writes the 400-character errors to state when validateData reports any, and only then opens the sign modal.
   *
   * @returns Nothing.
   */
  const handleClickSave = () => {
    if (!isEditComment) {
      showMessageInfo({ content: t`No changes were made.` });
      return;
    }

    const listError = validateData();
    if (!_.isEmpty(listError)) {
      setState({
        listError,
      });
      return;
    }

    setState({ isOpenSignModal: true });
  };

  /**
   * Closes the physician amendment notes drawer. Invokes the onClose prop with the drawer key when one was supplied.
   *
   * @returns Nothing.
   */
  const handleOnClose = () => {
    if (onClose) {
      onClose(drawerKey);
    }
  };

  useEffect(() => {
    if (!open) return;

    setState({
      ..._.cloneDeep(storeData.current),
      isInitiating: false,
      listError: {},
    });
  }, [open]);

  useEffect(() => {
    const omittedKeys = ['reports'];
    const dataToCompare = _.omit(_.cloneDeep(data), omittedKeys);
    const previousDataToCompare = _.omit(_.cloneDeep(previousPropsData.current), omittedKeys);

    if (!_.isEqual(dataToCompare, previousDataToCompare)) {
      previousPropsData.current = _.cloneDeep(data);
      initiateState();
    }
  }, [data]);

  return {
    state,
    setState,
    isAbortedStudy,
    isEditComment,
    handleChange,
    onEndSignature,
    handleClickSave,
    handleOnClose,
    onSave,
  };
};
